import React, { useMemo } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  createColumnHelper,
} from '@tanstack/react-table';
import { LinkButton } from 'components/design-system/atoms/AppLink';
import {
  Checkbox,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@cargomatic/ui';
import { unescapeHTML } from 'underscore.string';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import AccessorialRequestStatusBadge from 'components/v2/AccessorialRequestStatusBadge';
import formatMoney from 'utils/formatters/Money';
import { checkProcessedAccessorial } from 'helpers/accessorials';
import { sluggify } from 'utils/localization/utils';
import { TablePagination } from 'components/design-system/molecules/TablePagination';
import { ColumnHeader } from 'components/design-system/molecules/ColumnHeader';
import { TruncatedCell } from 'components/design-system/molecules/TruncatedCell';

const columnHelper = createColumnHelper();

function buildColumns({
  onShowShipmentModal,
  onShowAccessorialRequestPane,
  selectedIds,
  onSelect,
  onSelectAll,
  data,
  t,
}) {
  return [
    columnHelper.display({
      id: 'select',
      header: () => {
        const selectableRows = data.filter(
          (row) => !checkProcessedAccessorial(row),
        );
        const allSelected =
          selectableRows.length > 0 &&
          selectableRows.every((row) => selectedIds.includes(row._id));
        return (
          <Checkbox
            checked={allSelected}
            onCheckedChange={(value) => onSelectAll(!!value)}
            aria-label="Select all"
          />
        );
      },
      cell: ({ row }) => {
        const isSelected = selectedIds.includes(row.original._id);
        return (
          <Checkbox
            checked={isSelected}
            disabled={checkProcessedAccessorial(row.original)}
            onCheckedChange={(value) => onSelect(row.original, !!value)}
            aria-label="Select row"
          />
        );
      },
      meta: { className: 'min-w-10' },
      enableSorting: false,
    }),
    columnHelper.accessor('shipment_id', {
      header: 'Shipment',
      cell: ({ row }) => (
        <LinkButton
          className="whitespace-nowrap"
          onClick={() => {
            const s = row.original.shipment;
            onShowShipmentModal(typeof s === 'string' ? s : s?._id);
          }}
        >
          {row.original.shipment_id}
        </LinkButton>
      ),
      meta: { className: 'min-w-32' },
      enableSorting: false,
    }),
    columnHelper.accessor('equipment_id', {
      header: 'Equipment',
      cell: ({ getValue }) => <span>{unescapeHTML(getValue() || '')}</span>,
      meta: { className: 'min-w-28' },
      enableSorting: false,
    }),
    columnHelper.accessor('shipper_name', {
      header: 'Shipper',
      cell: ({ getValue }) => (
        <TruncatedCell text={unescapeHTML(getValue() || '')} />
      ),
      meta: { className: 'min-w-40' },
      enableSorting: false,
    }),
    columnHelper.accessor('carrier_name', {
      header: 'Carrier',
      cell: ({ getValue }) => (
        <TruncatedCell text={unescapeHTML(getValue() || '')} />
      ),
      meta: { className: 'min-w-40' },
      enableSorting: false,
    }),
    columnHelper.accessor('message', {
      header: 'Accessorial',
      cell: ({ row }) => (
        <LinkButton onClick={() => onShowAccessorialRequestPane(row.original)}>
          {row.original.message}
        </LinkButton>
      ),
      meta: { className: 'min-w-28' },
      enableSorting: false,
    }),
    columnHelper.accessor('shipper_cost', {
      header: 'Shipper Cost',
      cell: ({ row }) =>
        formatMoney(row.original.shipper_cost || 0, row.original?.currency),
      meta: { className: 'min-w-24' },
      enableSorting: false,
    }),
    columnHelper.accessor('receivable_rate_source', {
      header: 'Shipper Source',
      cell: ({ getValue }) => <span>{t(sluggify(getValue())) || ''}</span>,
      meta: { className: 'min-w-28' },
      enableSorting: false,
    }),
    columnHelper.accessor('carrier_cost', {
      header: 'Carrier Cost',
      cell: ({ row }) =>
        formatMoney(row.original.carrier_cost ?? 0, row.original?.currency),
      meta: { className: 'min-w-24' },
      enableSorting: false,
    }),
    columnHelper.accessor('payable_rate_source', {
      header: 'Carrier Source',
      cell: ({ getValue }) => <span>{t(sluggify(getValue())) || ''}</span>,
      meta: { className: 'min-w-28' },
      enableSorting: false,
    }),
    columnHelper.accessor('shipper_status', {
      header: 'Shipper Status',
      cell: ({ getValue }) => (
        <AccessorialRequestStatusBadge status={getValue()} />
      ),
      meta: { className: 'min-w-28' },
      enableSorting: false,
    }),
    columnHelper.accessor('carrier_status', {
      header: 'Carrier Status',
      cell: ({ getValue }) => (
        <AccessorialRequestStatusBadge status={getValue()} />
      ),
      meta: { className: 'min-w-28' },
      enableSorting: false,
    }),
    columnHelper.accessor('created_by', {
      header: 'Created By',
      meta: { className: 'min-w-40' },
      enableSorting: false,
    }),
    columnHelper.accessor('carrier_decline_reason_code', {
      header: 'Reason Code',
      meta: { className: 'min-w-28' },
      enableSorting: false,
    }),
    columnHelper.accessor('payee_type', {
      header: 'Payee',
      cell: ({ row }) => {
        const { payee_type, carrier_name, vendor_name, partner_name } =
          row.original;
        if (payee_type === 'carrier')
          return <TruncatedCell text={carrier_name || ''} />;
        if (payee_type === 'vendor')
          return <TruncatedCell text={vendor_name || ''} />;
        if (payee_type === 'partner')
          return <TruncatedCell text={partner_name || ''} />;
        return null;
      },
      meta: { className: 'min-w-40' },
      enableSorting: false,
    }),
    columnHelper.accessor('origin', {
      header: 'Origin Address',
      cell: ({ row }) => (
        <span>{row.original.shipment?.origin?.[0]?.address || ''}</span>
      ),
      meta: { className: 'min-w-56' },
      enableSorting: false,
    }),
    columnHelper.accessor('destination', {
      header: 'Destination Address',
      cell: ({ row }) => (
        <span>{row.original.shipment?.destination?.[0]?.address || ''}</span>
      ),
      meta: { className: 'min-w-56' },
      enableSorting: false,
    }),
    columnHelper.accessor('action_required', {
      header: 'Action Required',
      cell: ({ row }) => (
        <LinkButton onClick={() => onShowAccessorialRequestPane(row.original)}>
          {row.original.action_required}
        </LinkButton>
      ),
      meta: { className: 'min-w-36' },
      enableSorting: false,
    }),
    columnHelper.accessor('approval_due_date', {
      header: 'Approval Due Date',
      cell: ({ getValue }) => {
        const value = getValue();
        if (!value) return null;
        return <span>{moment(value).format('MMMM Do YYYY, h:mm:ss a')}</span>;
      },
      meta: { className: 'min-w-56' },
    }),
    columnHelper.accessor('delivery_by', {
      header: 'Delivery',
      cell: ({ row }) => {
        const { deliver_after, deliver_by } = row.original.shipment || {};
        if (!deliver_after || !deliver_by) return null;
        return (
          <div>
            <div>{moment(deliver_after).format('MMMM Do YYYY, h:mm:ss a')}</div>
            <div>{moment(deliver_by).format('MMMM Do YYYY, h:mm:ss a')}</div>
          </div>
        );
      },
      meta: { className: 'min-w-64' },
      enableSorting: false,
    }),
    columnHelper.accessor('created_at', {
      header: 'Requested',
      cell: ({ getValue }) => {
        const value = getValue();
        if (!value) return null;
        return (
          <span>
            {moment(value).format('MMMM Do YYYY, h:mm:ss a')}{' '}
            <span className="text-muted-foreground text-xs">
              ({t('TIMEZONE_NAME')})
            </span>
          </span>
        );
      },
      meta: { className: 'min-w-96' },
      enableSorting: false,
    }),
    columnHelper.accessor('modified_at', {
      header: 'Updated',
      cell: ({ row }) => {
        const time = row.original.modified_at || row.original.created_at;
        if (!time) return null;
        const now = moment();
        const timeToDiff = moment(time);
        const days = now.diff(timeToDiff, 'days');
        const hours = now.diff(timeToDiff, 'hours');
        const minutes = now.diff(timeToDiff, 'minutes');
        const seconds = now.diff(timeToDiff, 'seconds');
        let elapsed;
        if (days > 0) elapsed = `${days}d`;
        else if (hours > 0) elapsed = `${hours}h`;
        else if (minutes > 0) elapsed = `${minutes}m`;
        else elapsed = `${seconds}s`;
        return (
          <LinkButton
            onClick={() => onShowAccessorialRequestPane(row.original)}
          >
            {elapsed}
          </LinkButton>
        );
      },
      meta: { className: 'min-w-24' },
      enableSorting: false,
    }),
  ];
}

function AccessorialExceptionsTable({
  data,
  loading,
  selectedIds,
  onSelect,
  onSelectAll,
  page,
  pages,
  total,
  perPage,
  onPageChange,
  pageSizeOptions,
  onPageSizeChange,
  sortField,
  sortOrder,
  onSortChange,
  onShowShipmentModal,
  onShowAccessorialRequestPane,
  emptyText,
}) {
  const { t } = useTranslation();

  const columns = useMemo(
    () =>
      buildColumns({
        onShowShipmentModal,
        onShowAccessorialRequestPane,
        selectedIds,
        onSelect,
        onSelectAll,
        data,
        t,
      }),
    [
      onShowShipmentModal,
      onShowAccessorialRequestPane,
      selectedIds,
      onSelect,
      onSelectAll,
      data,
      t,
    ],
  );

  const sorting = sortField
    ? [{ id: sortField, desc: sortOrder !== 'asc' }]
    : [];

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualSorting: true,
    enableSortingRemoval: false,
    manualPagination: true,
    pageCount: pages,
    state: {
      sorting,
      pagination: {
        pageIndex: page - 1,
        pageSize: perPage,
      },
    },
    onSortingChange: (updater) => {
      const newSorting =
        typeof updater === 'function' ? updater(sorting) : updater;
      if (newSorting.length > 0) {
        onSortChange(newSorting[0].id, newSorting[0].desc ? 'desc' : 'asc');
      }
    },
  });

  return (
    <div>
      <div className="overflow-x-auto">
        <Table className="whitespace-nowrap [&_td]:align-top">
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead
                    key={header.id}
                    className={header.column.columnDef.meta?.className || ''}
                  >
                    {header.isPlaceholder ? null : header.column.getCanSort() ? (
                      <ColumnHeader
                        title={flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                        sorted={header.column.getIsSorted() || false}
                        onSort={(direction) => {
                          onSortChange(header.column.id, direction);
                        }}
                      />
                    ) : (
                      flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )
                    )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>
                  {columns.map((_, colIdx) => (
                    <TableCell key={colIdx}>
                      <Skeleton className="h-4 w-full" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : table.getRowModel().rows.length > 0 ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={
                    selectedIds.includes(row.original._id) && 'selected'
                  }
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      key={cell.id}
                      className={cell.column.columnDef.meta?.className || ''}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-left">
                  {emptyText}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      {!loading && total > 0 && (
        <TablePagination
          page={page}
          pages={pages}
          total={total}
          perPage={perPage}
          onPageChange={onPageChange}
          pageSizeOptions={pageSizeOptions}
          onPageSizeChange={onPageSizeChange}
        />
      )}
    </div>
  );
}

export default AccessorialExceptionsTable;
